<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between align-items-center">
        <h6 class="element-header">
          Kunden
        </h6>

        <Buttons/>
      </div>

      <div class="element-box">
        <Menu/>

        <Search :newAndActiveUnemployedDropdown="newAndActiveUnemployedDropdown"
                :aaNumberDropdown="aaNumberDropdown"
                :officeDropdown="officeDropdown"
                :filters="filters"
                :status="status"
                :loading="isSearching"
                :insuranceNumberDropdown="insuranceNumberDropdown"
                @search="getUnemployedList"
                @dropdown-fetch="handleDropdownSearch"
                @clear-dropdown="handleClearDropdown"/>

        <div class="row align-items-center justify-content-between mt-3">
          <div class="col-auto">
            <PerPage
                :values="[10, 25, 50, 100]"
                @update:perPage="getUnemployedList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>

          <div class="form-group mb-0 col-12 col-md-4 mt-4 mt-md-0">
            <input name="searchTerm"
                   class="form-control"
                   @input="handleDebounceSearch"
                   v-model="filterDebounce.searchTerm"
                   :placeholder="$t('general.button.search')"/>
          </div>
        </div>

        <EmptyState
            v-if="!unemployedList.data?.length"
            icon="fa-users"
            title="Keine Kunden gefunden"
            subtitle="Es wurden keine Kunden gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Kundennumer</th>
              <th>Status</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Standort</th>
              <th>Telefonnummer</th>
              <th>Erstellt am Tag</th>
              <th style="text-align: right; padding-right: 30px; min-width: 150px;">
                {{ $t('general.actions') }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(unemployed) in unemployedList.data" :key="unemployed.id">
              <td>{{ unemployed?.customerNumber }}</td>
              <td>{{ unemployed?.statusTranslated }}</td>
              <td>{{ unemployed?.firstName }}</td>
              <td>{{ unemployed?.lastName }}</td>
              <td>{{ unemployed?.location?.name }}</td>
              <td>{{ unemployed?.phone }}</td>
              <td>{{ unemployed?.createdAt }}</td>
              <td class="text-end">
                <EditButton :unemployedId="unemployed.id"/>
                <!--                <button class="btn btn-primary" @click="router.push({-->
                <!--                    name: 'unemployedPrivateCoachingList',-->
                <!--                    params: { id: unemployed?.id }-->
                <!--                  })">-->
                <!--                  <i class="os-icon os-icon-edit"></i>-->
                <!--                </button>-->
                <button class="btn btn-danger ms-2" @click="deleteModal(unemployed?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination v-if="unemployedList?.pagination?.total_pages > 1"
                    :totalPages="unemployedList?.pagination?.total_pages"
                    :currentPage="unemployedList?.pagination?.current_page"
                    :totalItems="unemployedList?.pagination?.total_items"
                    :perPage="unemployedList?.pagination?.items_per_page"
                    @pagechanged="getUnemployedList"/>
      </div>

      <ModalDelete
          v-model="showModalDelete"
          :header="t('general.delete_item_header')"
          :onDelete="deleteUnemployed"/>

    </div>
  </PageTemplate>
</template>

<script setup>
import unemployedService from '@/api/Unemployed';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {getNewAndActiveUnemployedDropdown, getOfficeDropdown} from '@/shared/dropdowns';
import {downloadFile} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Buttons from '@/views/App/Unemployed/List/Buttons.vue';
import EditButton from '@/views/App/Unemployed/List/EditButton.vue';
import Menu from '@/views/App/Unemployed/List/Menu.vue';
import Search from '@/views/App/Unemployed/List/NewAndActive/Search.vue';
import debounce from 'lodash/debounce';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const route = useRoute();
const sidebarSearchTerm = route.query.searchTerm;

const status = computed(() =>
    enums.unemployedStatus.filter((status) => status.value === 'new' || status.value === 'active',
    ),
);

const {enums} = piniaEnumsStore();
const {t} = useI18n();
const isLoaded = ref(false);
const unemployedList = ref([]);
const showModalDelete = ref(false);
const newAndActiveUnemployedDropdown = ref([]);
const officeDropdown = ref([]);
const aaNumberDropdown = ref([]);
const insuranceNumberDropdown = ref([]);
const isSearching = ref(false);
const unemployedId = ref(null);
const isExcelLoaded = ref(false);

const filters = ref({
  type: '',
  status: '',
  insuranceNumber: '',
  aaNumber: '',
  lastName: '',
  locationId: '',
});

const filterDebounce = ref({
  searchTerm: '',
});

const debouncedSearch = debounce(async (query) => {
  await getUnemployedList(1, undefined, filterDebounce.value);
}, 300);

const handleDebounceSearch = (event) => {
  debouncedSearch(event.target.value);
};

async function getUnemployedList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = unemployedList.value?.pagination?.items_per_page;

  try {
    const {data} = await unemployedService.getUnemployedNewAndActive({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });
    unemployedList.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function deleteModal(id) {
  unemployedId.value = null;
  CloseToast();
  showModalDelete.value = true;
  unemployedId.value = id;
}

async function deleteUnemployed() {
  try {
    filters.value = {
      status: '',
      interestedIn: '',
      lastName: '',
      contactPersonId: '',
    };

    showModalDelete.value = false;

    await unemployedService.deleteUnemployed(unemployedId.value);
    await getUnemployedList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

const handleDropdownSearch = async (searchTerm, type) => {
  if (searchTerm.length < 3) return; //

  const filters = {
    searchTerm: searchTerm,
    searchTermFor: type
  }

  if (type === 'lastName') await getNewAndActiveUnemployedDropdown(
      newAndActiveUnemployedDropdown,
      'lastName',
      'lastName',
      filters
  )

  if (type === 'aaNumber') await getNewAndActiveUnemployedDropdown(
      aaNumberDropdown,
      'aaNumber',
      'aaNumber',
      filters
  )

  if (type === 'insuranceNumber') await getNewAndActiveUnemployedDropdown(
      insuranceNumberDropdown,
      'insuranceNumber',
      'insuranceNumber',
      filters
  )
};

const handleClearDropdown = (type) => {
  if (type === 'lastName') newAndActiveUnemployedDropdown.value = [];
  if (type === 'aaNumber') aaNumberDropdown.value = []
  if (type === 'insuranceNumber') insuranceNumberDropdown.value = []
}

async function getExcelExport() {
  await downloadFile('/unemployed/new-and-active-export', undefined,
      {...filters.value, searchTerm: filterDebounce.value.searchTerm},
      false,
      'GET',
      isExcelLoaded
  )
}

onMounted(async () => {
  filterDebounce.value.searchTerm = sidebarSearchTerm ?? '';
  await Promise.all([
    getUnemployedList(1, undefined, filterDebounce.value),
    getOfficeDropdown(officeDropdown),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>
.nav.sm {
  width: max-content;
}
</style>